import { useCallback, useEffect, useRef } from 'react'

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLButtonElement

export default function useTabGroup() {
  const tabElements = useRef<Set<FieldElement>>(new Set())
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  function registerTabElement(ref: FieldElement | null): void {
    if (ref) {
      tabElements.current.add(ref)
    }
  }

  const focusNextElement = useCallback((triggerClick = false) => {
    const elementList = Array.from(tabElements.current)

    if (!document || elementList.length === 0) {
      return
    }

    const { activeElement } = document
    if (
      activeElement &&
      (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLSelectElement)
    ) {
      const selectedItemIndex = elementList.indexOf(activeElement)

      // for Horizon search form, focus() does not open the DatePicker
      const focusOrClick = triggerClick ? 'click' : 'focus'

      // possibly redundant but might prevent duplicate setTimeout's
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      if (selectedItemIndex === -1 || selectedItemIndex + 1 >= elementList.length) {
        timeoutRef.current = setTimeout(() => elementList[0][focusOrClick](), 0)
        return
      }
      timeoutRef.current = setTimeout(() => elementList[selectedItemIndex + 1][focusOrClick](), 0)
    }
  }, [])

  return {
    registerTabElement,
    focusNextElement,
  }
}
